import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useGoogleCalendar() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [calendarOwnerId, setCalendarOwnerId] = useState<string | null>(null);

  const connectGoogleCalendar = async (userId: string) => {
    try {
      setIsConnecting(true);

      // Call edge function to get OAuth URL
      const { data, error } = await supabase.functions.invoke('google-calendar-auth', {
        body: { user_id: userId },
      });

      if (error) throw error;

      // Open OAuth window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const authWindow = window.open(
        data.authUrl,
        'Google Calendar Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for success or error messages
      const handleMessage = async (event: MessageEvent) => {
        if (event.data.type === 'google_calendar_connected') {
          await checkConnection(userId);
          toast.success('Google Calendar conectado com sucesso!');
          window.removeEventListener('message', handleMessage);
          setIsConnecting(false);
        } else if (event.data.type === 'google_calendar_error') {
          toast.error(`Erro ao conectar: ${event.data.error}`);
          window.removeEventListener('message', handleMessage);
          setIsConnecting(false);
        }
      };

      window.addEventListener('message', handleMessage);

      // Check if window was closed without completing auth
      const checkClosed = setInterval(() => {
        if (authWindow?.closed) {
          clearInterval(checkClosed);
          setIsConnecting(false);
          window.removeEventListener('message', handleMessage);
        }
      }, 500);

    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      toast.error('Erro ao conectar Google Calendar');
    } finally {
      setIsConnecting(false);
    }
  };

  // Helper function to resolve the actual calendar owner
  const resolveCalendarOwner = async (userId: string): Promise<string> => {
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-connection-status', {
        body: { app_user_id: userId },
      });

      if (error) throw error;

      if (data?.connected && data?.owner_user_id) {
        return data.owner_user_id;
      }

      return userId;
    } catch (error) {
      console.error('Error resolving calendar owner:', error);
      return userId;
    }
  };

  const checkConnection = async (userId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-connection-status', {
        body: { app_user_id: userId },
      });

      if (error) throw error;

      const connected = data?.connected || false;
      const ownerId = data?.owner_user_id || null;

      setIsConnected(connected);
      setCalendarOwnerId(ownerId);

      return connected;
    } catch (error) {
      console.error('Error checking connection:', error);
      setIsConnected(false);
      setCalendarOwnerId(null);
      return false;
    }
  };

  const disconnectGoogleCalendar = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('google_calendar_tokens')
        .delete()
        .eq('app_user_id', userId);

      if (error) throw error;

      setIsConnected(false);
      toast.success('Google Calendar desconectado com sucesso!');
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      toast.error('Erro ao desconectar Google Calendar');
      throw error;
    }
  };

  const checkAvailability = async (params: {
    host_user_id: string;
    start_time: string;
    end_time: string;
  }) => {
    try {
      // Resolve the actual calendar owner
      const ownerId = await resolveCalendarOwner(params.host_user_id);

      const { data, error } = await supabase.functions.invoke('google-calendar-check-availability', {
        body: {
          ...params,
          host_user_id: ownerId,
        },
      });

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error checking availability:', error);
      
      if (error.message?.includes('not connected')) {
        return { 
          success: false, 
          connected: false, 
          error: 'Host não está conectado ao Google Calendar' 
        };
      }
      
      throw error;
    }
  };

  const createCalendarEvent = async (params: {
    host_user_id: string;
    title: string;
    description: string;
    start_time: string;
    end_time: string;
    location?: string;
    attendees: string[];
    meeting_id?: string;
  }) => {
    try {
      // Resolve the actual calendar owner
      const ownerId = await resolveCalendarOwner(params.host_user_id);

      const { data, error } = await supabase.functions.invoke('google-calendar-create-event', {
        body: {
          ...params,
          host_user_id: ownerId,
        },
      });

      if (error) throw error;

      toast.success('Convite enviado via Google Calendar!');
      return data;
    } catch (error: any) {
      console.error('Error creating calendar event:', error);
      
      if (error.message?.includes('not connected')) {
        toast.error('Host precisa conectar Google Calendar primeiro');
      } else {
        toast.error('Erro ao criar evento no calendário');
      }
      throw error;
    }
  };

  const deleteCalendarEvent = async (params: {
    host_user_id: string;
    event_id: string;
  }) => {
    try {
      // Resolve the actual calendar owner
      const ownerId = await resolveCalendarOwner(params.host_user_id);

      const { data, error } = await supabase.functions.invoke('google-calendar-delete-event', {
        body: {
          host_user_id: ownerId,
          event_id: params.event_id,
        },
      });

      if (error) throw error;

      toast.success('Evento removido do Google Calendar!');
      return data;
    } catch (error: any) {
      console.error('Error deleting calendar event:', error);
      
      if (error.message?.includes('not connected')) {
        toast.warning('Não foi possível remover do calendário (host não conectado)');
      } else {
        toast.error('Erro ao remover evento do calendário');
      }
      throw error;
    }
  };

  const updateCalendarEvent = async (params: {
    host_user_id: string;
    event_id: string;
    title: string;
    description?: string;
    start_time: string;
    end_time: string;
    location?: string;
    attendees?: string[];
  }) => {
    try {
      const ownerId = await resolveCalendarOwner(params.host_user_id);

      const { data, error } = await supabase.functions.invoke('google-calendar-update-event', {
        body: {
          ...params,
          host_user_id: ownerId,
        },
      });

      if (error) throw error;

      toast.success('Evento atualizado no Google Calendar!');
      return data;
    } catch (error: any) {
      console.error('Error updating calendar event:', error);
      toast.error('Erro ao atualizar evento no calendário');
      throw error;
    }
  };

  return {
    isConnecting,
    isConnected,
    connectGoogleCalendar,
    disconnectGoogleCalendar,
    checkConnection,
    checkAvailability,
    createCalendarEvent,
    deleteCalendarEvent,
    updateCalendarEvent,
  };
}